import React, { useEffect, useState, useRef } from "react";
// Import loadStripe from the Stripe JS library to initialize Stripe
import { loadStripe } from "@stripe/stripe-js";
// Import necessary Elements hooks and components from React Stripe JS wrapper
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
// Import Convex hooks to invoke our backend action
import { useAction } from "convex/react";
// Import API reference generated by Convex
import { api } from "../../../convex/_generated/api";

/**
 * Prop interface for the PaymentForm component.
 * - priceId: The Stripe Price ID representing the selected subscription plan tier.
 * - onSuccess: Optional callback triggered when the payment completes successfully.
 * - onSubscriptionCreated: Optional callback triggered when a draft subscription is registered in Stripe.
 * - onCancel: Optional callback triggered when a user chooses to abort/cancel the current checkout flow.
 */
export interface PaymentFormProps {
  priceId: string;
  onSuccess?: () => void;
  onSubscriptionCreated?: (subscriptionId: string) => void;
  onCancel?: () => void;
}

// Instantiate Stripe client once outside the component scope using our publishable env key.
// Non-coders: Ensure VITE_STRIPE_PUBLISHABLE_KEY is configured in your project's .env files.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "");

/**
 * Helper to generate a unique v4-compliant UUID in pure JavaScript.
 * Ensures the billing attempt tracker stays distinct across renders and prevents duplicate charges.
 */
const generateUUID = (): string => {
  if (typeof window !== "undefined" && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * StripeCheckoutForm (Child Component)
 * Consumes the Stripe context provided by the parent <Elements> container.
 * Handles the actual token collection and payment submission to Stripe's APIs.
 */
const StripeCheckoutForm: React.FC<{
  subscriptionId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}> = ({ subscriptionId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();

  // Component state to track in-flight actions, errors, and success states
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Non-coders: A ref updates synchronously. React state updates are asynchronous, meaning multiple
  // rapid clicks in the same event tick will see isSubmitting as false. Using a ref guarantees that
  // subsequent synchronous click/submission handlers abort immediately.
  const isSubmittingRef = useRef<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Prevent submission if Stripe elements have not loaded yet or if a payment submission is already in progress.
    // Non-coders: This guard checks the isSubmitting flag. We also check isSubmittingRef.current because state updates are async in React,
    // and a ref check ensures rapid synchronous double-clicks are blocked immediately.
    if (!stripe || !elements || isSubmitting || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);

    // Validate fields and submit element parameters before calling confirmation
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message || "Please check your payment details.");
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    try {
      // Confirm the Stripe payment intent, attempting direct in-page resolution when possible.
      // Non-coders: Change redirect: "if_required" if you want to ALWAYS force a redirect to another page.
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?success=true&sub=${subscriptionId}`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        // Stripe failed to charge or validate card
        setErrorMessage(result.error.message || "An unexpected payment error occurred.");
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      } else {
        // Payment succeeded without needing redirect!
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "An unexpected error occurred during confirmation.");
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-checkout-form" data-testid="stripe-checkout-form">
      {/* Stripe-managed dynamic payment fields (card number, expiry, cvc) */}
      <PaymentElement />

      {errorMessage && (
        <div className="payment-error-message" role="alert" style={{ color: "#FF0055", marginTop: "1rem", fontSize: "0.9rem" }}>
          {errorMessage}
        </div>
      )}

      {/* Checkout Control Buttons */}
      <div className="payment-form-actions" style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
        {onCancel && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={!stripe || isSubmitting}
          style={{ flex: 1 }}
        >
          {isSubmitting ? "Processing..." : "Pay Now"}
        </button>
      </div>
    </form>
  );
};

/**
 * PaymentForm (Parent Wrapper Component)
 * Initiates the client secret via Convex actions, manages the loading layout skeleton,
 * and sets up the Stripe <Elements> provider context.
 */
export const PaymentForm: React.FC<PaymentFormProps> = ({
  priceId,
  onSuccess,
  onSubscriptionCreated,
  onCancel,
}) => {
  // Convex hook to run our subscription initiator action
  const createSubscription = useAction(api.stripe.createSubscription);

  // States to keep track of Stripe client secret and associated IDs
  const [clientSecret, setClientSecret] = useState<string>("");
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [billingAttemptId, setBillingAttemptId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [initError, setInitError] = useState<string | null>(null);

  // Use a ref to prevent executing action calls twice in React StrictMode
  const initiatedRef = useRef<string | null>(null);

  useEffect(() => {
    // Return early if there is no valid Price ID selected
    if (!priceId) return;
    
    // Prevent duplicated calls for the same priceId during the same phase
    if (initiatedRef.current === priceId) {
      return;
    }
    
    // Lock the current pricing intent
    initiatedRef.current = priceId;

    const initiateCheckout = async () => {
      setLoading(true);
      setInitError(null);
      setClientSecret("");
      setSubscriptionId("");

      // Generate a stable billing attempt token
      const attemptId = generateUUID();
      setBillingAttemptId(attemptId);

      try {
        const response = await createSubscription({
          priceId,
          billingAttemptId: attemptId,
        });

        setClientSecret(response.clientSecret);
        setSubscriptionId(response.subscriptionId);

        if (onSubscriptionCreated) {
          onSubscriptionCreated(response.subscriptionId);
        }
      } catch (err) {
        // Reset lock on failure so the user can re-attempt
        initiatedRef.current = null;
        setInitError(
          err instanceof Error
            ? err.message
            : "Could not create payment session. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    initiateCheckout();
  }, [priceId, createSubscription, onSubscriptionCreated]);

  // Render a minimal height skeleton to prevent cumulative layout shifts (CLS)
  if (loading) {
    return (
      <div
        className="payment-form-skeleton"
        style={{
          minHeight: "350px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#161D30",
          borderRadius: "var(--border-radius-md)",
          padding: "2rem",
          boxShadow: "0 0 20px rgba(0, 212, 255, 0.1)",
        }}
        data-testid="payment-loading-skeleton"
      >
        <div className="loading-spinner" style={{ borderTopColor: "#00D4FF", width: "40px", height: "40px", border: "4px solid rgba(255,255,255,0.1)", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <p style={{ marginTop: "1rem", color: "var(--color-text-secondary)" }}>
          Loading secure checkout...
        </p>
      </div>
    );
  }

  if (initError) {
    return (
      <div
        className="payment-form-error-card"
        style={{
          minHeight: "350px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#161D30",
          borderRadius: "var(--border-radius-md)",
          padding: "2rem",
          textAlign: "center",
        }}
        data-testid="payment-init-error"
      >
        <p style={{ color: "#FF0055", fontWeight: "600", marginBottom: "1.5rem" }}>
          {initError}
        </p>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Back to Plans
        </button>
      </div>
    );
  }

  // Render nothing if we are in an idle state (no price selected or no secret generated)
  if (!clientSecret) {
    return null;
  }

  // Define customization colors to align Stripe Elements visual theme with our app
  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: "night" as const,
      variables: {
        colorPrimary: "#00D4FF",
        colorBackground: "#161D30",
      },
    },
  };

  return (
    <div
      className="payment-form-wrapper"
      style={{
        minHeight: "350px",
        backgroundColor: "#161D30",
        borderRadius: "var(--border-radius-md)",
        padding: "2rem",
        boxShadow: "0 0 20px rgba(0, 212, 255, 0.1)",
      }}
      data-testid="payment-form-wrapper"
      data-billing-attempt-id={billingAttemptId}
    >
      <h3 style={{ color: "#FFFFFF", marginBottom: "1.5rem", fontSize: "1.2rem" }}>
        Secure Payment Info
      </h3>
      {/* Elements provider feeds the Stripe client context down to StripeCheckoutForm */}
      <Elements stripe={stripePromise} options={elementsOptions}>
        <StripeCheckoutForm
          subscriptionId={subscriptionId}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </Elements>
    </div>
  );
};
